import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import PermitDetails from "./PermitDetails";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { createWarehouseInOutPermitSchema } from "../../../schemas/warehouseInOutPermitSchema";
import {
  createRef,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DescriptionOfWarehouseItem,
  STATUS_IDS,
  SYSTEM_DETAIL_CODES,
  USER_ROLES_IDS,
  Vehicle,
  WarehouseInOutPermitRequestDetails,
  warehouseInOutPermitttachmentKeys,
  WarehousePermitWorker,
} from "../../../constants/common";
import AccompanyingPersonnelDetails from "./AccompanyingPersonnelDetails";
import DescriptionOfWarehouseItems from "./DescriptionOfWarehouseItems";
import VehicleInformation from "./VehicleInformation";
import PermitValidity from "./PermitValidity";
import { formattedDateTime } from "../../../lib/functions";
import { APP_ROUTES } from "../../../routes";
import { useLocation } from "react-router-dom";
import { useGetAllSystemDetailCodesDropdown } from "../../../hooks/common/query";
import { useGetWarehouseInOutPermitById } from "../../../hooks/permit/query";
import Loader from "../../../components/loader";
import { cn } from "../../../utils/cn";
import useAuthStore from "../../../store/Auth";
import ActionButtonGroup from "./ActionButtonGroup";
import Header from "../Header";

interface FormData {
  exitFromBuilding?: string | null;
  entryIntoBuilding?: string | null;
  uploadEscortingEmployeeIDCard: any;
  escortEmployee: string;
  permitStartDate: string;
  permitEndDate: string;
  generalDescriptionOther?: string;
}

const WarehouseInOutPermit = () => {
  const { t, i18n } = useTranslation();

  const [addedWorkers, setAddedWorkers] = useState<WarehousePermitWorker[]>([]);

  const [
    addedDescriptionOfWarehouseItems,
    setAddedDescriptionOfWarehouseItems,
  ] = useState<DescriptionOfWarehouseItem[]>([]);

  const quantityOfItems = addedDescriptionOfWarehouseItems.reduce(
    (acc, item) => acc + Number(item.quantity || 0),
    0
  );

  const [addedVehicles, setAddedVehicles] = useState<Vehicle[]>([]);

  const { data: generalDescriptions } = useGetAllSystemDetailCodesDropdown({
    encryptedId:
      SYSTEM_DETAIL_CODES.WAREHOUSE_IN_OUT_PERMIT_GENERAL_DESCRIPTIONS,
  });

  const generalDescriptionOptions = useMemo(() => {
    return (
      generalDescriptions?.map(
        (item: {
          n_DetailedCode: number;
          s_Desc_E: string;
          s_Desc_A: string;
        }) => ({
          id: item.n_DetailedCode,
          label: i18n.language === "en" ? item.s_Desc_E : item.s_Desc_A,
        })
      ) || []
    );
  }, [generalDescriptions, i18n.language]);

  const [generalDescription, setGeneralDescription] = useState<number>(0);

  // Find the id for 'Other' option
  const otherOption = generalDescriptionOptions.find(
    (opt: { label: string }) => opt.label === "Other"
  );
  const isGeneralDescriptionOther =
    generalDescription === (otherOption ? otherOption.id : -1);

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const key = searchParams.get("ID");

  const { user } = useAuthStore();

  const { data: warehousePermit, isPending: isWarehousePermitLoading } =
    useGetWarehouseInOutPermitById({
      encryptedId: key || "",
    });

  const [isAttachments, setIsAttachments] = useState({
    haveEscortingEmployeeId: 0,
  });

  const {
    control,
    formState: { errors },
    clearErrors,
    handleSubmit,
    watch,
    setValue,
    setError,
  } = useFormWithSchema(
    createWarehouseInOutPermitSchema(isGeneralDescriptionOther, isAttachments),
    {
      reValidateMode: "onChange",
      defaultValues: {
        escortEmployee: user?.user_id?.toString(),
        permitStartDate:
          new Date() > new Date(new Date().setHours(7, 0, 0, 0))
            ? formattedDateTime(new Date())
            : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0))),
        permitEndDate: formattedDateTime(
          new Date(
            (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
              ? new Date()
              : new Date(new Date().setHours(7, 0, 0, 0))
            ).getTime() +
            60 * 60 * 1000
          )
        ),
      },
    }
  );

  const permitStartDate = new Date(watch("permitStartDate") || "");

  const uploadRefs = useRef<
    Record<string, MutableRefObject<HTMLInputElement | null>>
  >(
    warehouseInOutPermitttachmentKeys.reduce((acc, key) => {
      acc[key] = createRef<HTMLInputElement>();
      return acc;
    }, {} as Record<string, MutableRefObject<HTMLInputElement | null>>)
  );

  const [fileNames, setFileNames] = useState<Record<string, string>>(
    warehouseInOutPermitttachmentKeys.reduce(
      (acc, key) => ({ ...acc, [key]: "" }),
      {}
    )
  );

  const setFileName = (key: string, value: React.SetStateAction<string>) => {
    setFileNames((prev) => ({
      ...prev,
      [key]: typeof value === "function" ? value(prev[key]) : value,
    }));
  };

  const [permitDetails, setPermitDetails] =
    useState<WarehouseInOutPermitRequestDetails>();

  useEffect(() => {
    if (!warehousePermit) {
      setValue("exitFromBuilding", "");
      setValue("entryIntoBuilding", "");
      setValue(
        "permitStartDate",
        new Date() > new Date(new Date().setHours(7, 0, 0, 0))
          ? formattedDateTime(new Date())
          : formattedDateTime(new Date(new Date().setHours(7, 0, 0, 0)))
      );
      setValue(
        "permitEndDate",
        formattedDateTime(
          new Date(
            (new Date() > new Date(new Date().setHours(7, 0, 0, 0))
              ? new Date()
              : new Date(new Date().setHours(7, 0, 0, 0))
            ).getTime() +
            60 * 60 * 1000
          )
        )
      );
      setValue("generalDescriptionOther", "");

      setPermitDetails(undefined);
      setGeneralDescription(0);

      setIsAttachments({
        haveEscortingEmployeeId: 0,
      });

      setAddedWorkers([]);
      setAddedDescriptionOfWarehouseItems([]);
      setAddedVehicles([]);

      return;
    }
    const warehouseData = {
      id: warehousePermit.n_PermitWarehouseID,
      exitFromBuilding: warehousePermit.s_ExitBuilding,
      entryIntoBuilding: warehousePermit.s_EntryBuilding,
      escortEmployee: warehousePermit.n_UserID.toString(),

      permitStartDate: warehousePermit.dt_PermitStartDate,
      permitEndDate: warehousePermit.dt_PermitEndDate,

      generalDescriptionOther: warehousePermit.s_PermitDescriptionOthersValue,
    };

    setPermitDetails(warehouseData);

    Object.entries(warehouseData).forEach(([key, value]) => {
      setValue(key as keyof FormData, value);
    });

    setGeneralDescription(Number(warehousePermit.s_PermitDescriptionCode));

    setIsAttachments({
      haveEscortingEmployeeId: warehousePermit.haveUserCardFile ? 1 : 0,
    });

    setAddedWorkers(
      warehousePermit?.permitPersonnel.map(
        (person: {
          n_PermitPersonnelID: number;
          s_PersonnelName: string;
          nationality: string;
          s_Iqama: string;
          dt_IqamaIssue: string;
          s_IssuingAuthority: string;
          haveIqamaUpload: boolean;
        }) => ({
          id: person?.n_PermitPersonnelID,
          name: person.s_PersonnelName,
          nationality: person.nationality,
          residencePermitNo: person.s_Iqama,
          issueDate: person.dt_IqamaIssue,
          issuingAuthority: person.s_IssuingAuthority,
          uploadId: person.haveIqamaUpload,
        })
      )
    );

    setAddedDescriptionOfWarehouseItems(
      warehousePermit?.permitItems.map(
        (item: {
          n_PermitItemID: number;
          s_ItemType: string;
          n_Quantity: number;
          s_SupplyingOrExportingEntity: string;
        }) => ({
          id: item.n_PermitItemID,
          itemType: item.s_ItemType,
          quantity: item.n_Quantity,
          supplyingExportingEntityForPermitHolder:
            item.s_SupplyingOrExportingEntity,
        })
      )
    );

    setAddedVehicles(
      warehousePermit?.permitVehicles.map(
        (vehicle: {
          n_PermitVehicleID: number;
          s_DriverTempClientID: string;
          n_PlateSource: number;
          s_PlateCode: string;
          s_PlateNumber: string;
          s_Color: string;
          n_PlateLetter1: number;
          n_PlateLetter2: number;
          n_PlateLetter3: number;
          n_PermitPersonnelID: number;
          n_DriverPersonnelID: number;
          haveVehicleRegistrationFile: boolean;
        }) => {
          const personnel = warehousePermit?.permitPersonnel?.find(
            (worker: { n_PermitPersonnelID: number }) =>
              worker?.n_PermitPersonnelID === vehicle.n_DriverPersonnelID
          );

          return {
            temp: vehicle.n_DriverPersonnelID,
            id: vehicle.n_PermitVehicleID,
            plateSource: vehicle.n_PlateSource,
            plateNumber: vehicle.s_PlateNumber,
            plateLetter1: vehicle.n_PlateLetter1,
            plateLetter2: vehicle.n_PlateLetter2,
            plateLetter3: vehicle.n_PlateLetter3,
            color: vehicle.s_Color,
            personnelId: personnel?.n_PermitPersonnelID,
            driversName: personnel?.s_PersonnelName || "",
            uploadVehicleRegLicense: personnel?.haveVehicleRegistrationFile,
            driverTempClientID: vehicle.s_DriverTempClientID,
          };
        }
      )
    );
  }, [
    warehousePermit,
    setValue,
    setGeneralDescription,
    setAddedWorkers,
    setAddedDescriptionOfWarehouseItems,
    setAddedVehicles,
  ]);

  useEffect(() => {
    if (
      Object.keys(errors).length > 0 &&
      !(
        Object.keys(errors).length === 2 &&
        Object.keys(errors).includes("entryIntoBuilding") &&
        Object.keys(errors).includes("exitFromBuilding")
      )
    ) {
      toast.error(t("fillAllInfo"));
    }
  }, [errors, t]);

  const isPermitApprovedByNextApprover = useMemo(
    () =>
      !!(
        location.pathname === APP_ROUTES.UPDATE_WAREHOUSE_IN_OUT_PERMIT &&
        warehousePermit?.n_CurrentApproverOrderNo &&
        warehousePermit?.n_CurrentApproverOrderNo > 1
      ),
    [location.pathname, warehousePermit?.n_CurrentApproverOrderNo]
  );

  const isFieldsDisabled = useMemo(
    () =>
      (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
        user?.n_RoleID !== USER_ROLES_IDS.ADMIN) ||
      (warehousePermit &&
        warehousePermit?.n_ApprovalStatus !== STATUS_IDS.PENDING) ||
      (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
        isPermitApprovedByNextApprover) ||
      (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
        location.pathname === APP_ROUTES.APPROVE_WAREHOUSE_IN_OUT_PERMIT),
    [
      isPermitApprovedByNextApprover,
      location.pathname,
      user?.n_RoleID,
      warehousePermit,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-5">
        <Header title={t("warehouseInOutPermit")} data={warehousePermit} />

        {key && isWarehousePermitLoading ? (
          <div className="flex justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <PermitDetails
              control={control}
              errors={errors}
              clearErrors={clearErrors}
              isAttachments={isAttachments}
              uploadRefs={uploadRefs.current}
              fileNames={fileNames}
              setFileName={setFileName}
              generalDescription={generalDescription}
              setGeneralDescription={setGeneralDescription}
              generalDescriptionOptions={generalDescriptionOptions}
              permitDetails={permitDetails}
              isFieldsDisabled={isFieldsDisabled}
            />

            <AccompanyingPersonnelDetails
              addedWorkers={addedWorkers}
              setAddedWorkers={setAddedWorkers}
              isFieldsDisabled={isFieldsDisabled}
            />

            <DescriptionOfWarehouseItems
              addedDescriptionOfWarehouseItems={
                addedDescriptionOfWarehouseItems
              }
              setAddedDescriptionOfWarehouseItems={
                setAddedDescriptionOfWarehouseItems
              }
              isFieldsDisabled={isFieldsDisabled}
            />

            <VehicleInformation
              addedVehicles={addedVehicles}
              setAddedVehicles={setAddedVehicles}
              addedWorkers={addedWorkers}
              quantityOfItems={quantityOfItems}
              isFieldsDisabled={isFieldsDisabled}
            />

            <PermitValidity
              control={control}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              permitStartDate={permitStartDate}
              quantityOfItems={quantityOfItems}
              isFieldsDisabled={isFieldsDisabled}
            />

            <ActionButtonGroup
              warehousePermit={warehousePermit}
              handleSubmit={handleSubmit}
              setError={setError}
              generalDescription={generalDescription}
              addedWorkers={addedWorkers}
              addedDescriptionOfWarehouseItems={addedDescriptionOfWarehouseItems}
              addedVehicles={addedVehicles}
              isGeneralDescriptionOther={isGeneralDescriptionOther}
              isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
              isFieldsDisabled={isFieldsDisabled}
            />
          </>
        )}
      </div>
    </>
  );
};

export default WarehouseInOutPermit;
